/** Tiny DOM construction helpers — no framework, no virtual DOM, just speed. */

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number | boolean | ((...a: any[]) => void)> = {},
  ...children: (Node | string | null | undefined)[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    if (typeof v === 'function') {
      el.addEventListener(k.startsWith('on') ? k.slice(2) : k, v as EventListener);
    } else if (k === 'class') {
      el.className = String(v);
    } else if (k === 'text') {
      el.textContent = String(v);
    } else if (k === 'html') {
      el.innerHTML = String(v);
    } else if (v === true) {
      el.setAttribute(k, '');
    } else {
      el.setAttribute(k, String(v));
    }
  }
  for (const c of children) {
    if (c === null || c === undefined) continue;
    el.append(c);
  }
  return el;
}

export function button(label: string, onClick: () => void, cls = ''): HTMLButtonElement {
  const b = h('button', { class: cls, onclick: onClick, text: label });
  return b;
}

let toastRoot: HTMLDivElement | null = null;

export function toast(text: string, warn = false, ms = 3200): void {
  if (!toastRoot) {
    toastRoot = h('div', { id: 'toasts' });
    document.body.appendChild(toastRoot);
  }
  const t = h('div', { class: warn ? 'toast warn' : 'toast', text });
  toastRoot.appendChild(t);
  setTimeout(() => {
    t.style.transition = 'opacity 300ms';
    t.style.opacity = '0';
    setTimeout(() => t.remove(), 320);
  }, ms);
}

export function clear(el: HTMLElement): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/** Fragment builder that tolerates null/undefined children (conditional UI). */
export function frag(...children: (Node | string | null | undefined)[]): DocumentFragment {
  const f = document.createDocumentFragment();
  for (const c of children) {
    if (c !== null && c !== undefined) f.append(c);
  }
  return f;
}

/** Simple keyboard navigation across .focusable elements inside a screen. */
export class MenuNav {
  private items: HTMLElement[] = [];
  private index = -1;

  refresh(root: HTMLElement): void {
    this.items = [...root.querySelectorAll<HTMLElement>('.focusable')].filter(
      (e) => !(e as HTMLButtonElement).disabled && e.offsetParent !== null
    );
  }

  move(dir: 1 | -1): void {
    if (this.items.length === 0) return;
    this.index = (this.index + dir + this.items.length) % this.items.length;
    this.items.forEach((e, i) => e.classList.toggle('focus-nav', i === this.index));
    this.items[this.index]?.scrollIntoView({ block: 'nearest' });
  }

  confirm(): void {
    this.items[this.index]?.click();
  }

  clearFocus(): void {
    this.items.forEach((e) => e.classList.remove('focus-nav'));
    this.index = -1;
  }
}
